export type StudyMaterialPlacement = "materialsPage" | "lessonPage";

export type StudyMaterialLink = {
    /**
     * A normal destination, or a placement-specific tracking URL. Leave null
     * until a verified destination has been chosen.
     */
    url: string | null;
    /**
     * Set only when this exact destination is an affiliate URL. Keeping this
     * separate prevents ordinary links from being described as affiliate links.
     */
    affiliateUrl: string | null;
    /** Optional ID for a link provider that supports placement-level tracking. */
    trackingId: string | null;
    /** Optional button text for this link; falls back to DEFAULT_CTA_LABEL. */
    ctaLabel?: string;
};

export const DEFAULT_CTA_LABEL = "View book";

export type StudyMaterial = {
    id: string;
    title: string;
    type: string;
    description: string;
    suitableFor: string;
    recommendation: string;
    /** Optional JLPT level badge, e.g. "N5" - only set when a material is squarely aimed at one level. */
    jlptLevel?: string;
    /** Amazon.es paperback listing price in EUR. Confirm before adding a book. */
    price: number;
    details?: Array<{ label: string; value: string }>;
    coverImage?: { src: string; alt: string };
    links: Record<StudyMaterialPlacement, StudyMaterialLink>;
};

/** Prices below were checked against the linked Amazon.es paperback listings on 2026-10-06. */
export const STUDY_MATERIAL_PRICES_CHECKED_ON = "2026-10-06";

export const studyMaterials: StudyMaterial[] = [
    {
        id: "genki-1-textbook",
        title: "Genki I: An Integrated Course in Elementary Japanese",
        type: "Textbook",
        description:
            "A structured beginner textbook that introduces practical grammar, vocabulary, reading, listening, and everyday Japanese through connected lessons.",
        suitableFor: "Beginners studying approximately JLPT N5 / CEFR A1.",
        jlptLevel: "N5",
        recommendation:
            "This is the book I personally use in my online Japanese classes, and it was recommended by my teacher. Its lesson sequence makes it especially useful when you want clear explanations alongside regular practice.",
        details: [
            { label: "Edition", value: "Third Edition" },
            { label: "Volume", value: "Volume 1" },
            { label: "Language", value: "English" },
            { label: "Publisher", value: "The Japan Times Publishing" },
            { label: "Format", value: "Paperback" },
            { label: "ISBN-10 / ASIN", value: "4789017303" },
            { label: "ISBN-13", value: "9784789017305" },
            { label: "Coverage", value: "Lessons 1–12" },
        ],
        price: 43.76,
        coverImage: {
            src: "/images/genki-i-third-edition.jpg",
            alt: "Cover of GENKI I: An Integrated Course in Elementary Japanese, Third Edition, Volume 1",
        },
        links: {
            // Amazon.es affiliate short link (Amazon Associates).
            materialsPage: {
                url: null,
                affiliateUrl: "https://amzn.to/4z9o3AL",
                trackingId: null,
                ctaLabel: "Buy on Amazon.es",
            },
            lessonPage: { url: null, affiliateUrl: null, trackingId: null },
        },
    },
    {
        id: "genki-1-workbook",
        title: "Genki I Workbook",
        type: "Workbook",
        description:
            "Companion exercises for the Genki I lessons, with additional written, listening, and vocabulary practice to help turn new material into recallable skills.",
        suitableFor:
            "Genki I learners who benefit from guided homework, review between classes, or a more consistent independent study routine.",
        jlptLevel: "N5",
        recommendation:
            "I recommend using it alongside the textbook because the extra practice reinforces the same lesson sequence used in my online Japanese classes. It was part of the Genki approach my teacher recommended to me.",
        price: 24.13,
        coverImage: {
            src: "/images/genki-i-workbook.avif",
            alt: "Cover of the Genki I Workbook",
        },
        links: {
            // Amazon affiliate short link (Amazon Associates).
            materialsPage: {
                url: null,
                affiliateUrl: "https://amzn.to/4rqqr3i",
                trackingId: null,
                ctaLabel: "Buy on Amazon",
            },
            lessonPage: { url: null, affiliateUrl: null, trackingId: null },
        },
    },
    {
        id: "genki-1-answer-key",
        title: "GENKI: An Integrated Course in Elementary Japanese - Answer Key",
        type: "Answer Key",
        description:
            "The official answer key for the Genki I textbook and workbook exercises, so you can check your own work without waiting for a class to confirm it.",
        suitableFor:
            "Genki I learners studying independently, or wanting to self-check homework between classes.",
        jlptLevel: "N5",
        recommendation:
            "A straightforward way to confirm you're on the right track with the same textbook and workbook used in my online Japanese classes, especially useful for self-study between lessons.",
        details: [
            { label: "Edition", value: "Third Edition" },
            { label: "Volume", value: "Volume 1" },
            { label: "Publisher", value: "The Japan Times Publishing" },
            { label: "Format", value: "Paperback" },
            { label: "ISBN-10 / ASIN", value: "4789017362" },
        ],
        price: 16.30,
        coverImage: {
            src: "/images/genki-i-answer-key.avif",
            alt: "Cover of GENKI: An Integrated Course in Elementary Japanese - Answer Key, Third Edition",
        },
        links: {
            // Amazon.es affiliate short link (Amazon Associates).
            materialsPage: {
                url: null,
                affiliateUrl: "https://amzn.to/47bG7xR",
                trackingId: null,
                ctaLabel: "Buy on Amazon.es",
            },
            lessonPage: { url: null, affiliateUrl: null, trackingId: null },
        },
    },
    {
        id: "hiragana-from-zero-1",
        title: "Hiragana From Zero!: The Complete Japanese Hiragana Book, with integrated workbook and answer key: Volume 1 (Japanese Writing From Zero!)",
        type: "Hiragana book and workbook",
        description:
            "Learn to read and write hiragana with stroke-order guidance, handwriting practice, an integrated workbook and answer key.",
        suitableFor:
            "Beginners who want focused hiragana practice before reading longer Japanese texts.",
        recommendation:
            "Combines explanations, writing practice and answers in one book, so you can check your progress as you learn each character.",
        details: [
            { label: "Volume", value: "Volume 1" },
            { label: "Language", value: "English" },
            { label: "Authors", value: "George Trombley Jr., Yukari Takenaka" },
            { label: "ISBN-13", value: "9780976998174" },
        ],
        price: 11.18,
        coverImage: {
            src: "/images/hiragana-from-zero-1-cover.jpg",
            alt: "Cover of Hiragana From Zero! 1",
        },
        links: {
            // The supplied Amazon.es short link contains the nihongotech-21 affiliate tag.
            materialsPage: {
                url: null,
                affiliateUrl: "https://link.amazon/B0dFtmuWv",
                trackingId: null,
                ctaLabel: "Buy on Amazon.es",
            },
            lessonPage: { url: null, affiliateUrl: null, trackingId: null },
        },
    },
    {
        id: "katakana-from-zero-2",
        title: "Katakana From Zero!: The complete Japanese Katakana Book with integrated workbook and answer key.: Volume 2 (Japanese Writing From Zero!)",
        type: "Katakana book and workbook",
        description:
            "Learn to read and write katakana with stroke-order guidance, handwriting practice, an integrated workbook and answer key.",
        suitableFor:
            "Beginners who want focused katakana practice before reading longer Japanese texts.",
        recommendation:
            "Combines explanations, writing practice and answers in one book, so you can check your progress as you learn each character.",
        details: [
            { label: "Volume", value: "Volume 2" },
            { label: "Language", value: "English" },
            { label: "Authors", value: "George Trombley Jr., Yukari Takenaka" },
            { label: "ISBN-13", value: "9780976998181" },
        ],
        price: 11.18,
        coverImage: {
            src: "/images/katakana-from-zero-2-cover.jpg",
            alt: "Cover of Katakana From Zero! 2",
        },
        links: {
            materialsPage: {
                url: null,
                affiliateUrl: "https://link.amazon/B0gl8I8sb",
                trackingId: null,
                ctaLabel: "Buy on Amazon.es",
            },
            lessonPage: { url: null, affiliateUrl: null, trackingId: null },
        },
    },
    {
        id: "kanji-from-zero-1",
        title: "Kanji From Zero! 1: Proven Techniques to Learn Kanji with Integrated Workbook (Second Edition): Proven Techniques to Master Kanji Used by Students All Over the World.",
        type: "Kanji textbook and workbook",
        description:
            "Learn the first 240 kyouiku kanji and over 1,500 kanji words, with readings, stroke order and an integrated workbook.",
        suitableFor:
            "Learners who want to practice kanji with explanations in English.",
        recommendation:
            "Combines kanji readings, writing and compound words with workbook practice in one volume.",
        details: [
            { label: "Edition", value: "Second Edition" },
            { label: "Volume", value: "Volume 1" },
            { label: "Language", value: "English" },
            { label: "Authors", value: "George Trombley, Kanako Hatanaka, Yukari Takenaka, Justin McGowan" },
        ],
        price: 38.70,
        coverImage: {
            src: "/images/kanji-from-zero-1-second-edition.jpg",
            alt: "Cover of Kanji From Zero! 1, Second Edition",
        },
        links: {
            // The supplied Amazon short link resolves to nihongotech-21.
            materialsPage: {
                url: null,
                affiliateUrl: "https://link.amazon/B04LhycMg",
                trackingId: null,
                ctaLabel: "Buy on Amazon.es",
            },
            lessonPage: { url: null, affiliateUrl: null, trackingId: null },
        },
    },
];

export type StudyMaterialSort = "recommended" | "price-ascending" | "price-descending";

export function sortStudyMaterials(materials: StudyMaterial[], order: StudyMaterialSort): StudyMaterial[] {
    if (order === "recommended") return [...materials];

    return materials
        .map((material, index) => ({ material, index }))
        .sort((a, b) => {
            const difference = a.material.price - b.material.price;
            return (order === "price-ascending" ? difference : -difference) || a.index - b.index;
        })
        .map(({ material }) => material);
}

export function getStudyMaterialLink(material: StudyMaterial, placement: StudyMaterialPlacement) {
    const link = material.links[placement];
    const href = link.affiliateUrl || link.url;

    return {
        href,
        isAffiliate: Boolean(link.affiliateUrl),
        trackingId: link.trackingId,
        ctaLabel: link.ctaLabel ?? DEFAULT_CTA_LABEL,
    };
}
